import { isControl, REQUIRED_MARK, withLabel } from '../controls.ts';
import { type FilePickerOptions, filePicker } from '../file-picker.ts';
import { DECISION, esc, icon, quoteNumber, STATUS, shortId } from './format.ts';
import { workplace } from './session.ts';

/** Building blocks of every screen (escaped HTML): page header, empty state, pills, skeleton. */

/** The «Cargar más» row under a paged list, while the list goes on. */
export const loadMore = (list: string, more: boolean, shown: number, noun: string) =>
  more
    ? `<div class="load-more" data-component="load-more"><span class="muted small">Se muestran ${shown} ${noun}.</span><button type="button" class="btn" data-more="${esc(list)}">Cargar más</button></div>`
    : '';

export const pageHead = (title: string, description: string, actions = '') =>
  `<header class="page-head" data-component="page-header"><div><div class="eyebrow">${esc(workplace())}</div>
    <h1>${esc(title)}</h1><p>${description}</p></div>${actions ? `<div>${actions}</div>` : ''}</header>`;

/**
 * «Descargar Excel» and «Descargar PDF» of a report (the server builds it with what the role
 * may see). `what` completes the group's label: «Descargar la morosidad».
 */
export const reportButtons = (report: string, what: string) =>
  `<span class="report-actions" role="group" aria-label="Descargar ${esc(what)}">${(
    [
      ['xlsx', 'Excel'],
      ['pdf', 'PDF'],
    ] as const
  )
    .map(
      ([format, label]) =>
        `<button type="button" class="btn sm" data-report="${esc(report)}" data-format="${format}">${icon('download')}Descargar ${label}</button>`,
    )
    .join('')}</span>`;

/**
 * An empty state: icon, title, one sentence and the next action. Its title is an h3 inside a
 * section that has its own h2; `level` 2 when it hangs directly from the page's title (h1), so the
 * headings never skip a level (#242).
 */
export const empty = (
  iconName: string,
  title: string,
  text: string,
  action = '',
  level: 2 | 3 = 3,
) =>
  `<div class="card empty" data-component="empty-state">${icon(iconName)}<h${level}>${esc(title)}</h${level}><p>${text}</p>${action}</div>`;

export const pill = (state: string, label = STATUS[state] ?? DECISION[state] ?? state) =>
  `<span class="pill ${esc(state)}">${esc(label)}</span>`;

export const meter = (ratio: number, cls = '') =>
  `<span class="meter ${cls}" role="img" aria-label="${Math.round(ratio * 100)} %"><i style="width:${Math.max(0, Math.min(100, ratio * 100)).toFixed(1)}%"></i></span>`;

export const skel = (h: number, w = '100%') =>
  `<span class="skeleton" style="height:${h}px;width:${w}"></span>`;
/** A loading screen shaped like the one coming (F4): header, a row of tiles, a table. */
export const SKELETON = `<header class="page-head"><div style="display:grid;gap:10px;width:min(420px,100%)">${skel(12, '120px')}${skel(34, '70%')}${skel(14, '90%')}</div></header>
  <div class="kpis" data-component="kpis">${`<div class="card kpi">${skel(14, '50%')}${skel(28, '70%')}${skel(12, '60%')}</div>`.repeat(4)}</div>
  <div class="card" style="padding:20px;display:grid;gap:14px">${skel(16, '30%')}${skel(12)}${skel(12)}${skel(12, '80%')}</div>`;

/**
 * A labelled field: a control of the design system's layer (`controls.ts`) with the label as
 * its own (so the input inside it is named), in a `.field` box. `required`: the label carries
 * the required mark and the control `required` (#235).
 */
export const field = (label: string, control: string, opts: { required?: boolean } = {}) =>
  isControl(control)
    ? `<div class="field">${withLabel(control, label, opts)}</div>`
    : `<label class="field"><span>${label}${opts.required ? ` ${REQUIRED_MARK}` : ''}</span>${control}</label>`;

/**
 * A file field: the label (HTML, like `field`) above the design system's file picker. Not a
 * wrapping <label> like `field`, because the picker holds its own «Quitar» button.
 */
export const fileField = (label: string, picker: FilePickerOptions) =>
  `<div class="field"><label for="${esc(picker.id)}">${label}</label>${filePicker(picker)}</div>`;

/**
 * An internal id on screen (#235, M11): its short form in mono (`COT-…33bc850b`, readable over
 * the phone), the full id in its title and `data-id`; copying it copies the full id
 * (`bindIdCopy`).
 */
export const idRef = (id: string, shown = shortId(id)) =>
  `<span class="mono id-ref"${shown === id ? '' : ` title="${esc(id)}"`} data-id="${esc(id)}">${esc(shown)}</span>`;

/**
 * A quote on screen (#259): its agency's number (`COT-000123`) with the full id in its title and
 * copied whole; the short id when the answer does not carry the number.
 */
export const quoteRef = (q: { id: string; number?: number | null | undefined }) =>
  q.number ? idRef(q.id, quoteNumber(q.number)) : idRef(q.id);

/** Copying a short reference (alone, as a person selects it) puts the full id on the clipboard. */
export function bindIdCopy(root: Document) {
  root.addEventListener('copy', (e) => {
    const sel = root.getSelection?.();
    const node = sel?.rangeCount ? sel.getRangeAt(0).commonAncestorContainer : null;
    const el = node instanceof Element ? node : node?.parentElement;
    const ref = el?.closest<HTMLElement>('.id-ref');
    if (!ref?.dataset.id || !e.clipboardData) return;
    e.clipboardData.setData('text/plain', ref.dataset.id);
    e.preventDefault();
  });
}
